import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useChat } from "./ChatContext";
import { useAuth } from "../AuthContext";
import WarningModal from "../components/WarningModal/WarningModal";

// Gerçek zamanlı "müşteri kilidi".
// Bir kullanıcı bir müşteriyle çalışmaya başlayınca (o müşteriye ait bir modal/sayfa
// açınca) o müşteri diğer TÜM panel kullanıcıları için kilitlenir. Kimse aynı anda
// aynı müşteriye dokunamaz.
//
// KISIT: Soket sunucusu (server.js) harici; kilit kaydını orada tutamayız. Bu yüzden
// mevcut "status:update" yayın kanalı (ChatContext) üzerine PEER-TO-PEER / LEASE (kira)
// modeliyle kuruludur. Sunucu status:update'i gönderen hariç herkese yayınlar.
//
// Sağlamlık:
//  - heartbeat (renew): sahip periyodik yeniler; peer'ler TTL ile tutar.
//  - sweep: süresi geçen (sahip çöktü/kapandı) kilitleri otomatik düşürür.
//  - query/announce: yeni bağlanan istemci mevcut kilitleri öğrenir.
//  - inaktivite (5dk): sahip hareketsiz kalırsa kilit(ler) serbest bırakılır ve açık
//    ekran(lar)a "auto-close" sinyali gider.

const CustomerLockContext = createContext(null);
export const useCustomerLockContext = () => useContext(CustomerLockContext);

// GEÇİCİ TANI: soket relay/kilit akışını konsola basar. Sorun çözülünce false yap/kaldır.
// İstersen çalışırken: window.__CUSTOMER_LOCK_DEBUG__ = false; ile kapatılabilir.
const LOCK_DEBUG = true;
function dbg(...args) {
  if (!LOCK_DEBUG) return;
  if (typeof window !== "undefined" && window.__CUSTOMER_LOCK_DEBUG__ === false) return;
  // eslint-disable-next-line no-console
  console.log("%c[customer-lock]", "color:#eb5757;font-weight:700", ...args);
}

const ENTITY = "customer-lock";

// Sekme başına üretilen, sayfa ömrü boyunca sabit kimlik (kilit sahipliği bununla eşleşir).
const TAB_SESSION_ID = (() => {
  try {
    const existing = window.sessionStorage.getItem("customerLockTabId");
    if (existing) return existing;
    const generated = `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    window.sessionStorage.setItem("customerLockTabId", generated);
    return generated;
  } catch (e) {
    return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }
})();
const HEARTBEAT_MS = 20_000; // sahip her kilidi bu aralıkla yeniler
const LOCK_TTL_MS = 60_000; // yenileme gelmezse kilit bu süre sonunda düşer
const SWEEP_MS = 10_000; // süresi geçen kilit taraması + inaktivite kontrolü

function parsePositiveInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// 5 dk; REACT_APP_CUSTOMER_LOCK_IDLE_MS ile override edilebilir (test için düşürülür).
const IDLE_MS =
  (typeof process !== "undefined" &&
    parsePositiveInt(process.env.REACT_APP_CUSTOMER_LOCK_IDLE_MS)) ||
  300_000;

function normId(value) {
  return String(value ?? "").trim();
}

// Kilit kaydı SEKME bazında tutulur: locks[id].owners = { [sessionId]: {...} }.
// Aynı kullanıcının iki sekmesi aynı müşteriyi açtığında birinin kapanması (release)
// diğerinin kilidini düşürmemeli; tek kayıtlı eski yapıda son yayın kimin sessionId'sini
// taşıyorsa o sekmenin release'i kilidi HERKESTE siliyordu.
// Temsilci alanlar (userID/userName/at/expiresAt) eski şekli okuyan tüketiciler
// (hooks/useCustomerLock) için türetilir: en erken kilit alan sahip temsilcidir.
function buildLockEntry(owners) {
  const entries = Object.entries(owners || {});
  if (entries.length === 0) return null;
  const [, primary] = entries.reduce((best, cur) => (cur[1].at < best[1].at ? cur : best));
  return {
    userID: primary.userID,
    userName: primary.userName,
    at: primary.at,
    // Kilit, sahiplerinden HERHANGİ biri yaşadığı sürece ayakta kalır.
    expiresAt: entries.reduce((max, [, o]) => Math.max(max, o.expiresAt || 0), 0),
    owners,
  };
}

// Süresi geçmemiş ve BANA AİT OLMAYAN ilk sahip (yoksa null). Eski/temsilci-only
// kayıtlarda tek kayda düşülür.
function findOtherOwner(lock, myUserID) {
  if (!lock) return null;
  const owners = lock.owners;
  if (!owners) return String(lock.userID) !== String(myUserID) ? lock : null;
  const now = Date.now();
  for (const owner of Object.values(owners)) {
    if (owner?.expiresAt && owner.expiresAt < now) continue;
    if (String(owner?.userID) !== String(myUserID)) return owner;
  }
  return null;
}

function readMyUserId(fallback) {
  if (fallback != null && fallback !== "") return String(fallback);
  try {
    return String(
      localStorage.getItem("userID") || sessionStorage.getItem("userID") || ""
    ).trim();
  } catch (e) {
    return "";
  }
}

function readMyUserName() {
  try {
    return String(localStorage.getItem("username") || "").trim();
  } catch (e) {
    return "";
  }
}

// Etkinlik sayılan olaylar (useIdleLogout ile aynı set).
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

export function CustomerLockProvider({ children }) {
  const chat = useChat();
  const subscribeStatus = chat?.subscribeStatus;
  const emitStatusUpdate = chat?.emitStatusUpdate;
  const ready = chat?.ready;
  const myUserId = chat?.myUserId;

  // Merchant (partner) girişleri müşteri kilit sistemine HİÇ katılmaz: erişimleri her
  // zaman salt-inceleme olduğundan (a) hiçbir müşteriyi kilitlemez — kilit yaymaz, (b)
  // başkasının kilidiyle bloklanmaz — gelen kilitleri işlemez. Böylece merchant için
  // sistem tamamen atıl kalır. (İstek: "merchant giriş hiç bir şekilde müşteriyi
  // kitlememeli".) Ref ile tutulur ki broadcast/handleMessage callback kimlikleri
  // userType değişince yeniden üretilmesin.
  const { userType } = useAuth() || {};
  const isMerchantRef = useRef(false);
  isMerchantRef.current = userType === "merchant";

  // Başkalarının kilitleri (reaktif): { [customerID]: { userID, userName, at, expiresAt } }
  const [locks, setLocks] = useState({});
  const locksRef = useRef({});
  locksRef.current = locks;

  // Benim tuttuğum kilitler (ref-sayımlı): Map<customerID, { count, at }>
  const myLocksRef = useRef(new Map());

  // Otomatik-kapat aboneleri: Map<customerID, Set<cb>>
  const autoCloseRef = useRef(new Map());

  // Son kullanıcı etkinliği (inaktivite hesabı için).
  const lastActivityRef = useRef(Date.now());

  // Kimlik (her render güncel). sessionId: SEKME başına tek, kalıcı kimlik — kilit sahipliği
  // yalnız userID ile taşındığında aynı kullanıcının ikinci sekmesi modalı kapattığında
  // (release) kilit HERKESTE düşüyor, oysa ilk sekme müşteriyi hâlâ açık tutuyordu.
  const identityRef = useRef({ userID: "", userName: "", sessionId: TAB_SESSION_ID });
  identityRef.current = {
    userID: readMyUserId(myUserId),
    userName: readMyUserName(),
    sessionId: TAB_SESSION_ID,
  };

  // "Bu müşteri işleniyor" uyarısı (tek, global).
  const [warnOwner, setWarnOwner] = useState(null);

  const emitRef = useRef(null);
  emitRef.current = emitStatusUpdate;

  const broadcast = useCallback((action, customerID, at) => {
    // Merchant hiçbir kilit sinyali yaymaz (acquire/release/renew/query dâhil).
    if (isMerchantRef.current) return;
    const emit = emitRef.current;
    if (typeof emit !== "function") {
      dbg("SEND SKIPPED (emit yok — soket bağlı değil)", action, customerID);
      return;
    }
    const { userID, userName, sessionId } = identityRef.current;
    dbg("SEND", action, "id=", customerID || "*", "as userID=", userID || "(boş)");
    emit({
      entity: ENTITY,
      id: customerID || "*",
      action,
      userID,
      userName,
      sessionId,
      at: at ?? Date.now(),
    });
  }, []);

  const fireAutoClose = useCallback((id) => {
    const set = autoCloseRef.current.get(id);
    if (set) set.forEach((cb) => { try { cb(); } catch (e) { /* geç */ } });
  }, []);

  // ---- Genel API ----

  // Başkası tutuyorsa true. Kendi kilidim "başkası" sayılmaz.
  const isLockedByOther = useCallback((rawId) => {
    const id = normId(rawId);
    if (!id) return false;
    return !!findOtherOwner(locksRef.current[id], identityRef.current.userID);
  }, []);

  const getLock = useCallback((rawId) => {
    const id = normId(rawId);
    if (!id) return null;
    return findOtherOwner(locksRef.current[id], identityRef.current.userID);
  }, []);

  // Kilidi al. Başkası tutuyorsa { ok:false, blocked:true } döner (almaz).
  // Aynı müşteri için re-entrant (ref-sayımlı) — iç içe modal açımında tek kilit.
  const acquire = useCallback((rawId) => {
    const id = normId(rawId);
    if (!id) return { ok: false, blocked: false };
    // Merchant kilit tutmaz: ekranı açılsın diye ok döner ama kilit ne kaydedilir ne
    // yayılır (myLocksRef boş kalır → heartbeat/beforeunload da bir şey yapmaz).
    if (isMerchantRef.current) return { ok: true, blocked: false };
    if (findOtherOwner(locksRef.current[id], identityRef.current.userID)) {
      return { ok: false, blocked: true };
    }
    const mine = myLocksRef.current;
    const existing = mine.get(id);
    if (existing) {
      existing.count += 1;
      return { ok: true, blocked: false };
    }
    const at = Date.now();
    mine.set(id, { count: 1, at });
    broadcast("acquire", id, at);
    return { ok: true, blocked: false };
  }, [broadcast]);

  // Kilidi bırak (ref-sayımlı; 0'a inince yayınlar).
  const release = useCallback((rawId) => {
    const id = normId(rawId);
    if (!id) return;
    const mine = myLocksRef.current;
    const existing = mine.get(id);
    if (!existing) return;
    existing.count -= 1;
    if (existing.count > 0) return;
    mine.delete(id);
    broadcast("release", id, existing.at);
  }, [broadcast]);

  const subscribeAutoClose = useCallback((rawId, cb) => {
    const id = normId(rawId);
    if (!id || typeof cb !== "function") return () => {};
    const map = autoCloseRef.current;
    if (!map.has(id)) map.set(id, new Set());
    map.get(id).add(cb);
    return () => { map.get(id)?.delete(cb); };
  }, []);

  const notifyLocked = useCallback((rawId) => {
    const lock = getLock(rawId);
    setWarnOwner({ userName: lock?.userName || "" });
  }, [getLock]);

  // ---- Gelen mesaj işleyici ----
  const handleMessage = useCallback((data) => {
    // Merchant gelen kilitleri işlemez → `locks` boş kalır, hiçbir müşteride bloklanmaz.
    if (isMerchantRef.current) return;
    const action = data?.action;
    if (!action) return;
    const myId = String(identityRef.current.userID);
    const senderId = String(data?.userID ?? "");
    dbg("RECV", action, "id=", data?.id, "from userID=", senderId || "(boş)", "| benim userID=", myId || "(boş)");

    // Yeni istemci mevcut kilitleri soruyor → tuttuklarımı duyur.
    if (action === "query") {
      myLocksRef.current.forEach((info, cust) => broadcast("announce", cust, info.at));
      return;
    }

    const id = normId(data?.id);
    if (!id || id === "*") return;
    // Kendi SEKMEMİN yayını atlanır; aynı kullanıcının BAŞKA sekmesinin yayını işlenir
    // (kilit sahipliği sekme bazında tutuluyor).
    if (String(data?.sessionId ?? "") === String(identityRef.current.sessionId)) return;

    if (action === "release") {
      const senderSession = String(data?.sessionId ?? "");
      setLocks((prev) => {
        const cur = prev[id];
        if (!cur) return prev;
        const owners = cur.owners;
        // Yalnız yayını yapan SEKMENİN kaydı düşer; kullanıcının diğer sekmeleri hâlâ
        // sahipse kilit ayakta kalır. sessionId gelmeyen (eski) yayında o kullanıcının
        // tüm sekmeleri düşürülür — eski davranış.
        let nextOwners;
        if (!owners) {
          nextOwners = String(cur.userID) === senderId ? {} : null;
        } else if (senderSession) {
          if (!owners[senderSession]) return prev;
          nextOwners = { ...owners };
          delete nextOwners[senderSession];
        } else {
          nextOwners = Object.fromEntries(
            Object.entries(owners).filter(([, o]) => String(o?.userID) !== senderId)
          );
        }
        if (nextOwners === null) return prev;
        const entry = buildLockEntry(nextOwners);
        const next = { ...prev };
        if (entry) next[id] = entry;
        else delete next[id];
        return next;
      });
      return;
    }

    // acquire | renew | announce → sahiplik/yenileme
    const incomingAt = Number(data?.at) || Date.now();
    const mineInfo = myLocksRef.current.get(id);
    // Aynı KULLANICININ başka sekmesi: kilit zaten kendisinin, birbirini devirmez/kapatmaz.
    // (Kayıt tutulur ki release'te sahiplik sekme bazında eşleşsin.)
    if (mineInfo && senderId && senderId === myId) return;
    if (mineInfo) {
      // Çakışma: aynı müşteriyi ben de tutuyorum. Deterministik kazanan:
      // daha erken "at" (eşitse küçük userID) kazanır.
      const iWin =
        mineInfo.at < incomingAt ||
        (mineInfo.at === incomingAt && myId < senderId);
      if (iWin) return; // benimki geçerli; yok say
      // Kaybettim → devret: kendi kilidimi bırak, açık ekranı kapat.
      myLocksRef.current.delete(id);
      fireAutoClose(id);
    }

    // Sahip kaydı sekme bazında yazılır/yenilenir; diğer sekmelerin kaydı korunur.
    const ownerKey = String(data?.sessionId ?? "") || `user:${senderId}`;
    setLocks((prev) => {
      const owners = { ...(prev[id]?.owners || {}) };
      owners[ownerKey] = {
        userID: senderId,
        sessionId: String(data?.sessionId ?? ""),
        userName: data?.userName || "",
        at: incomingAt,
        expiresAt: Date.now() + LOCK_TTL_MS,
      };
      return { ...prev, [id]: buildLockEntry(owners) };
    });
  }, [broadcast, fireAutoClose]);

  // status:update (entity="customer-lock") aboneliği
  useEffect(() => {
    if (typeof subscribeStatus !== "function") {
      dbg("ABONELİK YOK — subscribeStatus fonksiyon değil (ChatProvider hazır değil?)");
      return undefined;
    }
    dbg("ABONE OLUNDU (entity=customer-lock)");
    return subscribeStatus(ENTITY, handleMessage);
  }, [subscribeStatus, handleMessage]);

  // Bağlantı hazır olunca: mevcut kilitleri sor + tuttuklarımı yeniden duyur
  // (reconnect'te diğerleri kilidimi TTL ile düşürmüş olabilir).
  useEffect(() => {
    if (!ready) return;
    dbg("SOKET HAZIR (ready) → query yayınlanıyor");
    broadcast("query", "*");
    myLocksRef.current.forEach((info, cust) => broadcast("renew", cust, info.at));
  }, [ready, broadcast]);

  // Etkinlik dinleyicileri + heartbeat + sweep + inaktivite + beforeunload
  useEffect(() => {
    const markActivity = () => { lastActivityRef.current = Date.now(); };
    const opts = { passive: true };
    ACTIVITY_EVENTS.forEach((e) => document.addEventListener(e, markActivity, opts));

    const heartbeat = setInterval(() => {
      myLocksRef.current.forEach((info, cust) => broadcast("renew", cust, info.at));
    }, HEARTBEAT_MS);

    const sweep = setInterval(() => {
      const now = Date.now();
      // Süresi geçen (sahip çöktü/kapandı) başkalarının kilitlerini düşür.
      setLocks((prev) => {
        let changed = false;
        const next = {};
        for (const [k, v] of Object.entries(prev)) {
          const owners = v.owners;
          if (!owners) {
            if (v.expiresAt && v.expiresAt < now) { changed = true; continue; }
            next[k] = v;
            continue;
          }
          // TTL sahip (sekme) başına işler: süresi geçen sekme düşer, diğerleri kalır.
          const liveOwners = Object.fromEntries(
            Object.entries(owners).filter(([, o]) => !(o?.expiresAt && o.expiresAt < now))
          );
          if (Object.keys(liveOwners).length === Object.keys(owners).length) {
            next[k] = v;
            continue;
          }
          changed = true;
          const entry = buildLockEntry(liveOwners);
          if (entry) next[k] = entry;
        }
        return changed ? next : prev;
      });
      // İnaktivite: kilit tutuyorken 5dk hareketsizlik → hepsini bırak + ekranı kapat.
      if (myLocksRef.current.size > 0 && now - lastActivityRef.current >= IDLE_MS) {
        Array.from(myLocksRef.current.keys()).forEach((cust) => {
          const info = myLocksRef.current.get(cust);
          myLocksRef.current.delete(cust);
          if (info) broadcast("release", cust, info.at);
          fireAutoClose(cust);
        });
      }
    }, SWEEP_MS);

    const onBeforeUnload = () => {
      myLocksRef.current.forEach((info, cust) => broadcast("release", cust, info.at));
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, markActivity, opts));
      clearInterval(heartbeat);
      clearInterval(sweep);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [broadcast, fireAutoClose]);

  const myId = readMyUserId(myUserId);
  const value = useMemo(
    () => ({
      locks,
      myUserId: myId,
      acquire,
      release,
      isLockedByOther,
      getLock,
      subscribeAutoClose,
      notifyLocked,
    }),
    [locks, myId, acquire, release, isLockedByOther, getLock, subscribeAutoClose, notifyLocked]
  );

  return (
    <CustomerLockContext.Provider value={value}>
      {children}
      <WarningModal
        isOpen={!!warnOwner}
        title="Müşteri Başka Bir Kullanıcıda Açık"
        message={
          warnOwner?.userName
            ? `"${warnOwner.userName}" tarafından işlem yapılıyor, şuan düzenleme yapılamaz.`
            : "Başka bir kullanıcı tarafından işlem yapılıyor, şuan düzenleme yapılamaz."
        }
        onClose={() => setWarnOwner(null)}
        onConfirm={() => setWarnOwner(null)}
        onConfirmText="Tamam"
        showCloseButton
      />
    </CustomerLockContext.Provider>
  );
}
